import React from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import FastInput from '../ui/FastInput';
import { cn } from '../../lib/utils';

const INDIAN_STATES = [
    "Andaman and Nicobar Islands",
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chandigarh",
    "Chhattisgarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Ladakh",
    "Lakshadweep",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal"
];

const CustomerForm = ({ customer, onChange, onCustomerSearch, errors = {} }) => {
    return (
        <div className="space-y-4">
            <h4 className="text-lg font-semibold">Customer Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Customer Name <span className="text-rose-500">*</span></Label>
                    <FastInput
                        type="text"
                        placeholder="Customer Name"
                        value={customer?.customerName || ''}
                        onValueChange={(val) => onChange('customerName', val)}
                        className={cn("h-12 rounded-xl border-slate-200 transition-all font-bold", errors.customerName && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}
                    />
                    {errors.customerName && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{errors.customerName}</p>}
                </div>
                <div className="space-y-2">
                    <Label>Contact Number <span className="text-rose-500">*</span></Label>
                    <FastInput
                        type="tel"
                        maxLength={10}
                        placeholder="Contact Number"
                        value={customer?.customerContactNumber || ''}
                        onValueChange={(val) => onChange('customerContactNumber', val)}
                        onBlur={(e) => onCustomerSearch && onCustomerSearch(e.target.value)} // Trigger search on blur
                        className={cn("h-12 rounded-xl border-slate-200 transition-all font-bold", errors.customerContactNumber && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}
                    />
                    {errors.customerContactNumber && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{errors.customerContactNumber}</p>}
                    <p className="text-xs text-muted-foreground italic font-semibold">
                        💡 Enter existing customer's phone number to auto-fill details
                    </p>
                </div>
                <div className="space-y-2">
                    <Label>Email</Label>
                    <FastInput
                        type="email"
                        placeholder="Email"
                        value={customer?.customerEmail || ''}
                        onValueChange={(val) => onChange('customerEmail', val)}
                    />
                </div>
                <div className="space-y-2">
                    <Label>Customer GST Number</Label>
                    <FastInput
                        type="text"
                        placeholder="GST Number (Optional)"
                        value={customer?.customerGSTNumber || ''}
                        onValueChange={(val) => onChange('customerGSTNumber', val)}
                    />
                </div>
            </div>

            <h4 className="text-lg font-semibold mt-6">Billing Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                    <Label>Address Line <span className="text-rose-500">*</span></Label>
                    <FastInput
                        type="text"
                        placeholder="Billing Address"
                        value={customer?.billingAddressLine || ''}
                        onValueChange={(val) => onChange('billingAddressLine', val)}
                        className={cn("h-12 rounded-xl border-slate-200 transition-all font-bold", errors.billingAddressLine && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}
                    />
                    {errors.billingAddressLine && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{errors.billingAddressLine}</p>}
                </div>
                <div className="space-y-2">
                    <Label>City <span className="text-rose-500">*</span></Label>
                    <FastInput
                        type="text"
                        placeholder="City"
                        value={customer?.billingCity || ''}
                        onValueChange={(val) => onChange('billingCity', val)}
                        className={cn("h-12 rounded-xl border-slate-200 transition-all font-bold", errors.billingCity && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}
                    />
                    {errors.billingCity && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{errors.billingCity}</p>}
                </div>
                <div className="space-y-2">
                    <Label>State <span className="text-rose-500">*</span></Label>
                    <Select
                        value={customer?.billingState || 'Maharashtra'}
                        onValueChange={(value) => onChange('billingState', value)}
                    >
                        <SelectTrigger className={cn("w-full h-12 rounded-xl border-slate-200 transition-all font-bold", errors.billingState && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}>
                            <SelectValue placeholder="Select State" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            {INDIAN_STATES.map((state) => (
                                <SelectItem key={state} value={state}>
                                    {state}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {errors.billingState && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{errors.billingState}</p>}
                </div>
                <div className="space-y-2">
                    <Label>Pincode <span className="text-rose-500">*</span></Label>
                    <FastInput
                        type="text"
                        placeholder="Pincode"
                        value={customer?.billingPincode || ''}
                        onValueChange={(val) => onChange('billingPincode', val)}
                        className={cn("h-12 rounded-xl border-slate-200 transition-all font-bold", errors.billingPincode && "border-rose-400 bg-rose-50/30 ring-2 ring-rose-500/10")}
                    />
                    {errors.billingPincode && <p className="text-xs font-black text-rose-500 ml-1 uppercase">{errors.billingPincode}</p>}
                </div>
            </div>

            <h4 className="text-lg font-semibold mt-6">Shipping Address (Optional)</h4>
            <div className="flex items-center space-x-2 mb-3">
                <input
                    type="checkbox"
                    id="sameAsBilling"
                    onChange={(e) => {
                        if (e.target.checked) {
                            // Copy billing to shipping
                            onChange('shippingAddressLine', customer?.billingAddressLine || '');
                            onChange('shippingCity', customer?.billingCity || '');
                            onChange('shippingState', customer?.billingState || 'Maharashtra');
                            onChange('shippingPincode', customer?.billingPincode || '');
                        } else {
                            // Clear shipping fields
                            onChange('shippingAddressLine', '');
                            onChange('shippingCity', '');
                            onChange('shippingState', 'Maharashtra');
                            onChange('shippingPincode', '');
                        }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="sameAsBilling" className="text-sm font-medium cursor-pointer">
                    Same as Billing Address
                </Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                    <Label>Address Line</Label>
                    <FastInput
                        type="text"
                        placeholder="Shipping Address (leave empty to use billing address)"
                        value={customer?.shippingAddressLine || ''}
                        onValueChange={(val) => onChange('shippingAddressLine', val)}
                    />
                </div>
                <div className="space-y-2">
                    <Label>City</Label>
                    <FastInput
                        type="text"
                        placeholder="City"
                        value={customer?.shippingCity || ''}
                        onValueChange={(val) => onChange('shippingCity', val)}
                    />
                </div>
                <div className="space-y-2">
                    <Label>State</Label>
                    <Select
                        value={customer?.shippingState || 'Maharashtra'}
                        onValueChange={(value) => onChange('shippingState', value)}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select State" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            {INDIAN_STATES.map((state) => (
                                <SelectItem key={state} value={state}>
                                    {state}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Pincode</Label>
                    <FastInput
                        type="text"
                        placeholder="Pincode"
                        value={customer?.shippingPincode || ''}
                        onValueChange={(val) => onChange('shippingPincode', val)}
                    />
                </div>
            </div>
        </div>
    );
};

export default React.memo(CustomerForm);